import { useCallback, useRef, useState } from 'react';
import { SimpleCache } from '../../utils/simple-cache';
import { type ResultSuppliers } from '../product-router';
import { type PreQuerySupplierArgs } from '../product-router/product/result-provider/result-provider-types';
import { type SearchItems } from '../product-router/product/result-types';
import {
	type DecoratorResultSetStateProps,
	useDecorateWithResultAndAPISetState,
} from './use-decorate-with-result-and-api-set-state';
import { ExperiencePerformanceTypes, ExperienceTypes } from '@atlassian/ufo';
import { COMPONENT, UFOExperienceOneTime } from '../../common/ufo-experiences';

/**
 * This decorator adds capabilities of API state tracking and caching to the plain PreQuerySupplier.
 * Think of this as an internal private method of useProductStateMachine.
 *
 * @returns A cached implementation of PreQuery Supplier which is also aware of setting its respective API state.
 */
export const usePreQueryDecorator = ({
	preQueryItemSupplier,
	setAPIState,
	setState,
	id,
}: Pick<ResultSuppliers, 'preQueryItemSupplier'> &
	Pick<DecoratorResultSetStateProps<{}>, 'setState' | 'setAPIState'> & {
		id: string;
	}) => {
	const [preQueryCache] = useState<SimpleCache<Promise<SearchItems>, [PreQuerySupplierArgs]>>(
		new SimpleCache(preQueryItemSupplier),
	);

	const searchItemSupplier = useCallback(
		(args: any) => preQueryCache.getAsync(args).value,
		[preQueryCache],
	);

	const ufoPreQueryLoadExperience = useRef<UFOExperienceOneTime>(
		new UFOExperienceOneTime(`${id}-pre-query-results-load`, {
			type: ExperienceTypes.Experience,
			performanceType: ExperiencePerformanceTypes.InlineResult,
			platform: { component: COMPONENT },
		}),
	);

	return useDecorateWithResultAndAPISetState({
		setState,
		setAPIState,
		searchItemSupplier,
		ufoExperience: ufoPreQueryLoadExperience.current,
	});
};
